/* ─── Live Chat — Adaptador desde chatEngine.ts ─── */
/* Todas las interfaces y datos legacy se preservan.                  */
/* Consume el motor unificado de mensajería.                          */

import {
  type CanonicalSource,
  type CanonicalMessageType,
  type CanonicalMessageStatus,
  type CanonicalMessage,
  canonicalSenders,
  canonicalMessages,
  getSender,
  detectType,
} from '@/mocks/chatEngine';

/* ════════════════════════════════════════════
   TIPOS LEGACY (conservados para compatibilidad)
   ════════════════════════════════════════════ */

export type ChatSource = CanonicalSource;

export type ChatMessageType = CanonicalMessageType;

export type ChatMessageStatus = CanonicalMessageStatus;

export interface UnifiedChatMessage {
  id: number;
  source: ChatSource;
  sourceDetail: string;
  senderName: string;
  senderAvatar: string;
  senderIP: string;
  content: string;
  type: ChatMessageType;
  status: ChatMessageStatus;
  priority: 'normal' | 'alta' | 'urgente';
  timestamp: string;
  songRequest?: string;
  contestAnswer?: string;
}

export type AdminColumn = 'todos' | 'cabina' | 'live' | 'concursos' | 'favoritos' | 'revision';

export const adminColumnLabels: Record<AdminColumn, string> = {
  todos: 'Todos',
  cabina: 'Cabina',
  live: 'Live',
  concursos: 'Concursos',
  favoritos: 'Favoritos',
  revision: 'En revisión',
};

export const adminColumnIcons: Record<AdminColumn, string> = {
  todos: 'ri-inbox-line',
  cabina: 'ri-broadcast-line',
  live: 'ri-live-line',
  concursos: 'ri-trophy-line',
  favoritos: 'ri-star-line',
  revision: 'ri-search-eye-line',
};

export type HostColumn = 'importantes' | 'favoritos' | 'saludos' | 'canciones' | 'pendientes';

export const hostColumnLabels: Record<HostColumn, string> = {
  importantes: 'Importantes',
  favoritos: 'Favoritos',
  saludos: 'Saludos',
  canciones: 'Canciones',
  pendientes: 'Pendientes',
};

export const hostColumnIcons: Record<HostColumn, string> = {
  importantes: 'ri-pushpin-line',
  favoritos: 'ri-star-line',
  saludos: 'ri-user-heart-line',
  canciones: 'ri-music-line',
  pendientes: 'ri-time-line',
};

export const sourceLabels: Record<ChatSource, string> = {
  cabina: 'Cabina',
  live: 'Live',
  concursos: 'Concursos',
  facebook: 'Facebook',
  youtube: 'YouTube',
};

export const sourceIcons: Record<ChatSource, string> = {
  cabina: 'ri-broadcast-line',
  live: 'ri-live-line',
  concursos: 'ri-trophy-line',
  facebook: 'ri-facebook-circle-fill',
  youtube: 'ri-youtube-fill',
};

export const sourceColors: Record<ChatSource, { bg: string; text: string; dot: string }> = {
  cabina: { bg: 'bg-secondary-100', text: 'text-secondary-700', dot: 'bg-secondary-500' },
  live: { bg: 'bg-rose-100', text: 'text-rose-700', dot: 'bg-rose-500' },
  concursos: { bg: 'bg-amber-100', text: 'text-amber-700', dot: 'bg-amber-500' },
  facebook: { bg: 'bg-sky-100', text: 'text-sky-700', dot: 'bg-sky-500' },
  youtube: { bg: 'bg-red-100', text: 'text-red-700', dot: 'bg-red-500' },
};

export const typeLabels: Record<ChatMessageType, string> = {
  saludo: 'Saludo',
  solicitud_cancion: 'Canción',
  peticion: 'Petición',
  concurso: 'Concurso',
  queja: 'Queja',
  urgente: 'Urgente',
  spam: 'Spam',
  revision: 'Revisión',
};

export const typeIcons: Record<ChatMessageType, string> = {
  saludo: 'ri-user-heart-line',
  solicitud_cancion: 'ri-music-line',
  peticion: 'ri-megaphone-line',
  concurso: 'ri-trophy-line',
  queja: 'ri-emotion-unhappy-line',
  urgente: 'ri-alert-line',
  spam: 'ri-spam-line',
  revision: 'ri-search-eye-line',
};

export const typeColors: Record<ChatMessageType, { bg: string; text: string }> = {
  saludo: { bg: 'bg-rose-100', text: 'text-rose-700' },
  solicitud_cancion: { bg: 'bg-emerald-100', text: 'text-emerald-700' },
  peticion: { bg: 'bg-violet-100', text: 'text-violet-700' },
  concurso: { bg: 'bg-amber-100', text: 'text-amber-700' },
  queja: { bg: 'bg-orange-100', text: 'text-orange-700' },
  urgente: { bg: 'bg-red-100', text: 'text-red-700' },
  spam: { bg: 'bg-foreground-200', text: 'text-foreground-600' },
  revision: { bg: 'bg-sky-100', text: 'text-sky-700' },
};

export const statusLabels: Record<ChatMessageStatus, string> = {
  nuevo: 'Nuevo',
  favorito: 'Favorito',
  fijado: 'Fijado',
  pendiente: 'Pendiente',
  leido_al_aire: 'Leído al aire',
  archivado: 'Archivado',
  bloqueado: 'Bloqueado',
};

export const statusColors: Record<ChatMessageStatus, { dot: string; text: string; bg: string }> = {
  nuevo: { dot: 'bg-primary-500', text: 'text-primary-700', bg: 'bg-primary-100' },
  favorito: { dot: 'bg-amber-400', text: 'text-amber-700', bg: 'bg-amber-100' },
  fijado: { dot: 'bg-violet-500', text: 'text-violet-700', bg: 'bg-violet-100' },
  pendiente: { dot: 'bg-orange-500', text: 'text-orange-700', bg: 'bg-orange-100' },
  leido_al_aire: { dot: 'bg-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-100' },
  archivado: { dot: 'bg-foreground-400', text: 'text-foreground-500', bg: 'bg-foreground-100' },
  bloqueado: { dot: 'bg-red-500', text: 'text-red-700', bg: 'bg-red-100' },
};

export const priorityIcons: Record<string, string> = {
  normal: 'ri-arrow-down-line',
  alta: 'ri-arrow-up-line',
  urgente: 'ri-alert-fill',
};

export const priorityColors: Record<string, string> = {
  normal: 'text-foreground-400',
  alta: 'text-amber-500',
  urgente: 'text-red-500',
};

/* ════════════════════════════════════════════
   MAPEO CANÓNICO → LEGACY
   ════════════════════════════════════════════ */

function toLegacyMessage(canonical: CanonicalMessage): UnifiedChatMessage {
  const sender = getSender(canonical.senderId);
  return {
    id: canonical.id,
    source: canonical.source,
    sourceDetail: canonical.sourceDetail,
    senderName: sender.name,
    senderAvatar: sender.avatarUrl,
    senderIP: sender.ip,
    content: canonical.content,
    type: canonical.type,
    status: canonical.status,
    priority: canonical.priority,
    timestamp: canonical.timestamp,
    songRequest: canonical.songRequest,
    contestAnswer: canonical.contestAnswer,
  };
}

/* ════════════════════════════════════════════
   DATOS EXPORTADOS (formato legacy)
   ════════════════════════════════════════════ */

export const unifiedMessages: UnifiedChatMessage[] = canonicalMessages.map(toLegacyMessage);

/* ════════════════════════════════════════════
   DETECCIÓN DE TIPO (delega al motor)
   ════════════════════════════════════════════ */

export function detectMessageType(content: string): ChatMessageType {
  return detectType(content);
}

/* ════════════════════════════════════════════
   HELPERS DE FILTRADO
   ════════════════════════════════════════════ */

export function getColumnMessages(messages: UnifiedChatMessage[], column: AdminColumn): UnifiedChatMessage[] {
  switch (column) {
    case 'todos':
      return messages.filter((m) => m.status !== 'bloqueado' && m.status !== 'archivado');
    case 'cabina':
      return messages.filter((m) => m.source === 'cabina' && m.status !== 'bloqueado' && m.status !== 'archivado');
    case 'live':
      return messages.filter((m) => m.source === 'live' && m.status !== 'bloqueado' && m.status !== 'archivado');
    case 'concursos':
      return messages.filter((m) => m.source === 'concursos' && m.status !== 'bloqueado' && m.status !== 'archivado');
    case 'favoritos':
      return messages.filter((m) => m.status === 'favorito' || m.status === 'fijado');
    case 'revision':
      return messages.filter((m) => m.type === 'revision' && m.status !== 'bloqueado' && m.status !== 'archivado');
  }
}

export function getHostColumnMessages(messages: UnifiedChatMessage[], column: HostColumn): UnifiedChatMessage[] {
  switch (column) {
    case 'importantes':
      return messages.filter((m) => m.status === 'fijado' || m.status === 'favorito' || m.priority === 'urgente');
    case 'favoritos':
      return messages.filter((m) => m.status === 'favorito');
    case 'saludos':
      return messages.filter((m) => m.type === 'saludo' && m.status !== 'bloqueado' && m.status !== 'archivado');
    case 'canciones':
      return messages.filter((m) => m.type === 'solicitud_cancion' && m.status !== 'bloqueado' && m.status !== 'archivado');
    case 'pendientes':
      return messages.filter((m) => m.status === 'pendiente' || m.status === 'nuevo');
  }
}